import _ from 'lodash';
import { AvailabilityStatus, EmployeeTimeOffAvailabilityRecord } from '../types/EmployeeTimeOff';
import { DateTime } from 'luxon';
import { Event } from '../types/Event';
import { IEventData } from '../pages/EmployeesSchedule/EmployeesSchedule';

// Maps an approval state to a UI availability status.
export const getAvailabilityStatus = (approved: boolean | undefined): AvailabilityStatus => {
  if (_.isNil(approved)) {
    return AvailabilityStatus.AVAILABLE;
  }
  return approved ? AvailabilityStatus.UNAVAILABLE : AvailabilityStatus.REQUESTED_OFF;
};

// Availability endpoint returns records per day. For now the first record
// determines the status shown in the UI for that day.
export const getAvailabilityStatusFromRecords = (records: EmployeeTimeOffAvailabilityRecord[]): AvailabilityStatus => {
  if (_.isEmpty(records)) {
    return AvailabilityStatus.AVAILABLE;
  }
  return _.some(records, 'approved') ? AvailabilityStatus.UNAVAILABLE : AvailabilityStatus.REQUESTED_OFF;
};

// Shared status metadata used throughout availability-related UIs.
export const AVAILABILITY_STATUS_CONFIG = {
  [AvailabilityStatus.AVAILABLE]: {
    label: 'Available',
    color: 'success.main',
    backgroundColor: 'transparent',
  },
  [AvailabilityStatus.REQUESTED_OFF]: {
    label: 'Requested Off',
    color: 'warning.main',
    backgroundColor: 'rgba(255, 152, 0, 0.06)',
  },
  [AvailabilityStatus.UNAVAILABLE]: {
    label: 'Unavailable',
    color: 'error.main',
    backgroundColor: 'rgba(244, 67, 54, 0.06)',
  },
} as const;

// Calendar columns are constrained to the visible Monday-Sunday range.
export const boundedCol = (col: number) => Math.min(8, Math.max(col, 1));

// Converts an event date range into calendar grid column positions.
// startCol is inclusive, endCol is exclusive.
// An event touching any portion of a day occupies that day's column.
export const calcEventCols = (weekStart: DateTime, event: Event): { startCol: number; endCol: number } => {
  const [eventStart, eventEnd] = _.map([event.start_at, event.end_at], (d) => DateTime.fromISO(d));

  const [startDayDiff, endDayDiff] = _.map([eventStart, eventEnd], (d) => d.diff(weekStart, ['hours']).hours / 24);

  return {
    startCol: boundedCol(1 + Math.floor(startDayDiff)),
    endCol: boundedCol(1 + Math.ceil(endDayDiff)),
  };
};

// Determines the background color for the day.
export const getAvailabilityConfigFromRecords = (records?: EmployeeTimeOffAvailabilityRecord[]) => {
  const status = getAvailabilityStatusFromRecords(records ?? []);

  return AVAILABILITY_STATUS_CONFIG[status];
};

// Returns true when multiple events occupy the same day column.
export const overlappingEvents = (events: IEventData[], dayCount: number): boolean =>
  _.some(_.range(1, dayCount + 1), (col) => _.filter(events, (event) => event.startCol <= col && col < event.endCol).length > 1);

// Packs events into rows so overlapping events render on separate lines.
export const buildEventRows = (events: IEventData[]): IEventData[][] => {
  const rows: IEventData[][] = [];

  _.forEach(_.sortBy(events, ['startCol', 'endCol']), (eventData) => {
    const availableRow = _.find(rows, (row) =>
      _.every(row, (placedEvent) => placedEvent.endCol <= eventData.startCol || eventData.endCol <= placedEvent.startCol)
    );

    if (availableRow) {
      availableRow.push(eventData);
    } else {
      rows.push([eventData]);
    }
  });

  return rows;
};
